import { useInfiniteQuery } from '@tanstack/react-query';

import { client } from '@/api/common';

import type { PartnerSubscription } from './types';

type Variables = {
  text?: string;
  locationIds?: number[];
  statuses?: string[];
  startDatetime?: string;
  endDatetime?: string;
  perPage?: number;
  enabled?: boolean;
};

type SubscriptionsPage = {
  data: PartnerSubscription[];
  meta: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
};

const fetchSubscriptions = async ({
  pageParam,
  variables,
}: {
  pageParam: number;
  variables: Variables;
}): Promise<SubscriptionsPage> => {
  const response = await client.get('v2/internal/partners/subscriptions', {
    params: {
      page: pageParam,
      per_page: variables.perPage ?? 10,
      text: variables.text || undefined,
      location_ids: variables.locationIds,
      statuses: variables.statuses,
      start_datetime: variables.startDatetime,
      end_datetime: variables.endDatetime,
    },
  });

  return response.data;
};

export const useSubscriptions = (variables: Variables) => {
  // pull out the enabled flag from the variables object as not to include it in the query key
  const { enabled = true, ...queryVariables } = variables;

  return useInfiniteQuery({
    queryKey: ['internal/partners/subscriptions', queryVariables],
    queryFn: ({ pageParam = 1 }) =>
      fetchSubscriptions({ pageParam, variables: queryVariables }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.page >= lastPage.meta.totalPages) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled,
  });
};
